import { useQuery } from '@tanstack/react-query';

import { getPropertyValue } from '@/types/crm';

import { createCRMObjectClient } from '../../core/CRMObjectClient';
import type { CrmObject } from '../../core/types';
import {
  enrichWithSpeakerMap,
  parseSpeakerMap,
} from '../utils/meetingEnrichment';
import { ReadMoreText, SummaryCardShell } from './summaryPanelShell';

export function MeetingNotesSummaryPanel({ object }: { object: CrmObject }) {
  const orgId = object.org_id;
  const meetingEntityId = object.entity_id ?? '';

  const result = useQuery({
    queryKey: ['aiSummaryMeetingNotes', orgId, meetingEntityId],
    enabled: !!meetingEntityId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const client = createCRMObjectClient(orgId, 'ahoy_meeting_notes');
      const res = await client.list({
        associated_with_entity_id: meetingEntityId,
        properties: ['ahoy_summary', 'ahoy_speaker_map', 'ahoy_created_at'],
        order_by: '-ahoy_created_at',
        limit: 1,
      });
      return res.results;
    },
  });

  const { data, error } = result;

  const note = data && data.length > 0 ? data[0] : null;
  const summary = note
    ? getPropertyValue<string>(note.properties?.ahoy_summary)
    : null;

  if (note && summary && summary.trim()) {
    const speakerMap = parseSpeakerMap(
      getPropertyValue<string>(note.properties?.ahoy_speaker_map)
    );
    const enriched = enrichWithSpeakerMap(summary, speakerMap);

    return (
      <SummaryCardShell>
        <ReadMoreText text={enriched} orgId={orgId} />
      </SummaryCardShell>
    );
  }

  if (error) {
    return (
      <SummaryCardShell>
        <div
          role='alert'
          aria-label="We couldn't load the summary"
          className='text-body-small text-[var(--color-text-error-primary)]'
        >
          Couldn&apos;t load the AI summary. Try again later.
        </div>
      </SummaryCardShell>
    );
  }

  return null;
}
